<template>
  <div v-if="loading && !hire" class="tw-flex tw-justify-center tw-py-8" data-test="hire-panel-loading">
    <WdsSpinner :size="24" indeterminate />
  </div>

  <WdsAlert v-else-if="error" error data-test="hire-panel-error">
    {{ $t('The hire could not be loaded. Try refreshing the page.') }}
  </WdsAlert>

  <WdsText v-else-if="!hire" secondary data-test="hire-panel-not-found">
    {{ $t('This hire could not be found.') }}
  </WdsText>

  <WdsStack v-else direction-column gap-md>
    <slot name="top">
      <HireHeaderBreadcrumbs
        :back-label="backLabel"
        :number="hire.number"
        data-test="hire-panel-top"
        @back="emit('back')"
      >
        <template #actions>
          <WdsButtonGhost
            secondary
            class="tw-text-primary tw-font-medium"
            :href="hireUrl"
            target="_blank"
            rel="noopener"
            data-test="hire-panel-view-hire"
          >
            <WdsStack align-center gap-xs>
              {{ $t('View hire') }}
              <WdsIcon name="arrow-up-right" :size="16" primary />
            </WdsStack>
          </WdsButtonGhost>
        </template>
      </HireHeaderBreadcrumbs>
    </slot>

    <WdsStack direction-column gap-sm>
      <WdsStack align-start justify-space-between gap-sm>
        <WdsHeading type="title2" data-test="hire-panel-title">{{ hire.job.name }}</WdsHeading>
        <HireActiveStatusLabel :status="hire.activeStatus" class="tw-flex-none" />
      </WdsStack>

      <WdsStack v-if="contract" align-center gap-xs>
        <WdsIcon name="calendar" :size="16" color="#757585" />
        <WdsText sm data-test="hire-panel-dates">
          <HireDateRange :start="timeLine.start" :end="timeLine.end" :pending-extension="hire.pendingExtension" />
        </WdsText>
      </WdsStack>

      <div
        v-if="hire.pendingExtension"
        class="tw-flex tw-items-start tw-gap-2 tw-rounded tw-border tw-border-solid tw-border-orange-200 tw-bg-orange-50 tw-px-3 tw-py-2.5"
        data-test="hire-panel-extended-note"
      >
        <WdsIcon name="arrow-right" :size="16" color="#B55D1A" class="tw-mt-0.5 tw-flex-none" />
        <WdsText sm>{{ extensionNote }}</WdsText>
      </div>

      <ClampedText v-if="contract?.jobDescription" overlay :text="contract.jobDescription" />
    </WdsStack>

    <!-- WdsTabs renders two roots, so this wrapper keeps the stack gap from splitting them. -->
    <div>
      <WdsTabs v-model:active-tab-key="activeTab">
        <WdsTab :title="$t('Overview')" tab-key="overview">
          <slot name="overview" :hire-id="hireId">
            <HirePanelOverview :hire-id="hireId" :custom-fields-answered-by="customFieldsAnsweredBy" />
          </slot>
        </WdsTab>
        <WdsTab v-if="hasChecklists" :title="$t('Checklists')" tab-key="checklists">
          <template #title>
            <TabTitleBadge
              :title="$t('Checklists')"
              :count="`${checkCounts.done}/${checkCounts.total}`"
            />
          </template>
          <slot name="checklists" :hire-id="hireId">
            <HireChecklists :hire-id="hireId" />
          </slot>
        </WdsTab>
      </WdsTabs>
    </div>
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsButtonGhost,
  WdsHeading,
  WdsIcon,
  WdsSpinner,
  WdsStack,
  WdsTab,
  WdsTabs,
  WdsText,
} from '@worksome/wds-components'
import HireChecklists from '@/components/Checklists/HireChecklists.vue'
import { useHireChecklists } from '@/components/Checklists/useHireChecklists'
import ClampedText from '@/components/Common/ClampedText.vue'
import TabTitleBadge from '@/components/Common/TabTitleBadge.vue'
import HireActiveStatusLabel from '@/components/Common/HireActiveStatusLabel.vue'
import HireDateRange from '@/components/Hire/HireDateRange.vue'
import HirePanelOverview from '@/components/Hire/HirePanelOverview.vue'
import { useHireContract } from '@/components/Hire/composables/useHireContract'
import { HireCustomFieldsAnsweredBy } from '@/components/Hire/composables/useHireCustomFieldSections'
import HireHeaderBreadcrumbs from '@/components/Hire/HirePageParts/HireHeaderBreadcrumbs.vue'
import { graphql } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useContractTimeLine } from '@/composables/useContractTimeLine'
import useDate from '@/composables/useDate'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'

// Give backLabel to show a back link (such as "All hires") and handle it through the back event.
const props = defineProps<{
  hireId: string
  backLabel?: string
  customFieldsAnsweredBy?: HireCustomFieldsAnsweredBy
}>()

const emit = defineEmits<{ back: [] }>()

const hireUrl = computed(() =>
  laravelNamedRoute(namedRoutes.hires.hire.view, { hire: props.hireId, tab: 'overview' }),
)

const activeTab = ref('overview')

const { result, loading, error } = useQuery(
  graphql(`
    query HirePanel($id: ID!) {
      hire(id: $id) {
        id
        number
        activeStatus
        pendingExtension {
          previousEndDate
          newEndDate
        }
        job {
          id
          name
        }
        ...HireCurrentContract
      }
    }
  `),
  () => ({ id: props.hireId }),
  { clientId: 'publicApi' },
)

const hire = computed(() => result.value?.hire ?? null)

const contract = useHireContract(hire)

const timeLine = useContractTimeLine(contract)

const extensionNote = computed(() =>
  hire.value?.pendingExtension
    ? $t('Extended to :date, awaiting signature', {
        date: useDate(hire.value.pendingExtension.newEndDate).date.value,
      })
    : '',
)

const { hasChecklists, checkCounts } = useHireChecklists(() => props.hireId)

// A tab that no longer exists would leave the panel blank, so fall back to the overview.
watch(hasChecklists, (has) => {
  if (!has && activeTab.value === 'checklists') activeTab.value = 'overview'
})
</script>
